import React, {useEffect, useRef, useState} from "react";
import {FieldLabel} from "../Onboarding/registrationShared";
import TextFieldBox from "../../components/Common/TextFieldBox";
import {validateEmail} from "../../helper/validation";
import {resendVerificationEmailApi} from "../../api/emailVerificationApi";
import useResendCooldown from "../../hooks/useResendCooldown";
import {
  AccessibleFieldLabel,
  FeedbackText,
  FormStack,
  PrimaryButton,
  ValidationText,
  VisuallyHidden,
} from "./styled";

const COOLDOWN_STORAGE_KEY = "email-verification-resend-cooldown";
const SUCCESS_COOLDOWN_SECONDS = 60;
const VALIDATION_ERROR_ID = "verification-email-validation-error";

export default function ResendVerificationForm({initialEmail = "", initialCooldownSeconds = 0}) {
  const [email, setEmail] = useState(initialEmail);
  const [validationError, setValidationError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const emailFieldLabel = useRef(null);
  const requestInFlight = useRef(false);
  const {remainingSeconds, feedback, setFeedback, beginCooldown} = useResendCooldown({
    storageKey: COOLDOWN_STORAGE_KEY,
    initialCooldownSeconds,
  });

  useEffect(() => {
    const input = emailFieldLabel.current?.querySelector("input");
    if (!input) return;

    if (validationError) {
      input.setAttribute("aria-describedby", VALIDATION_ERROR_ID);
      input.setAttribute("aria-invalid", "true");
      return;
    }

    input.removeAttribute("aria-describedby");
    input.removeAttribute("aria-invalid");
  }, [validationError]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (requestInFlight.current || remainingSeconds > 0) return;

    const normalizedEmail = email.toLowerCase().trim();
    const error = validateEmail(normalizedEmail);
    setValidationError(error);
    if (error) return;

    requestInFlight.current = true;
    setSubmitting(true);
    const response = await resendVerificationEmailApi(normalizedEmail);
    requestInFlight.current = false;
    setSubmitting(false);

    if (response?.status === 200) {
      beginCooldown(SUCCESS_COOLDOWN_SECONDS, response.data?.message || "Request acknowledged.");
      return;
    }

    const retryAfterSeconds = response?.data?.detail?.retry_after_seconds;
    if (response?.status === 429 && Number.isFinite(retryAfterSeconds)) {
      beginCooldown(retryAfterSeconds, "Please wait before requesting another verification email.");
      return;
    }

    setFeedback("We could not confirm your request. Check your connection and try again.");
  };

  return (
    <FormStack onSubmit={handleSubmit}>
      <FieldLabel required>Email address</FieldLabel>
      <AccessibleFieldLabel ref={emailFieldLabel}>
        <VisuallyHidden>Email address</VisuallyHidden>
        <TextFieldBox
          variant="outlined"
          type="email"
          placeholder="Email address"
          name="email"
          value={email}
          handleChange={(event) => setEmail(event.target.value)}
          fullWidth
          disabled={submitting}
        />
      </AccessibleFieldLabel>
      {validationError && (
        <ValidationText id={VALIDATION_ERROR_ID} role="alert">
          {validationError}
        </ValidationText>
      )}
      <PrimaryButton
        data-testid="verify-resend-button"
        variant="contained"
        type="submit"
        disabled={submitting || remainingSeconds > 0}
        fullWidth
      >
        {submitting ? "Requesting..." : "Send a new verification email"}
      </PrimaryButton>
      {remainingSeconds > 0 && (
        <FeedbackText data-testid="verify-resend-cooldown" aria-live="polite">
          {feedback} Try again in {remainingSeconds} seconds.
        </FeedbackText>
      )}
      {feedback && remainingSeconds === 0 && (
        <FeedbackText role="status">{feedback}</FeedbackText>
      )}
    </FormStack>
  );
}
